/**
 * Service Area Utilities
 * Converts between GeoJSON (map format) and custom format (with bounds for WordPress)
 */

export interface ServiceAreaPolygon {
  type: 'polygon'
  polygons: [number, number][][] // Array of polygons, each with [lat, lng] pairs
  center: { lat: number; lng: number }
  bounds: {
    lat_min: number
    lat_max: number
    lng_min: number
    lng_max: number
  }
}

export interface ServiceAreaRadius {
  type: 'radius'
  center: { lat: number; lng: number }
  radius: number
  bounds: {
    lat_min: number
    lat_max: number
    lng_min: number
    lng_max: number
  }
}

export type ServiceArea = ServiceAreaPolygon | ServiceAreaRadius

/**
 * Check if two polygons are duplicates (same coordinates)
 */
function arePolygonsDuplicate(poly1: [number, number][], poly2: [number, number][]): boolean {
  if (poly1.length !== poly2.length) return false
  
  // Check if all points are the same (allowing small floating point differences)
  const tolerance = 0.0001
  for (let i = 0; i < poly1.length; i++) {
    const latDiff = Math.abs(poly1[i][0] - poly2[i][0])
    const lngDiff = Math.abs(poly1[i][1] - poly2[i][1])
    if (latDiff > tolerance || lngDiff > tolerance) {
      return false
    }
  }
  return true
}

/**
 * Remove duplicate polygons
 */
function deduplicatePolygons(polygons: [number, number][][]): [number, number][][] {
  const unique: [number, number][][] = []
  
  for (const polygon of polygons) {
    const isDuplicate = unique.some(existing => arePolygonsDuplicate(existing, polygon))
    if (!isDuplicate) {
      unique.push(polygon)
    }
  }
  
  return unique
}

/**
 * Convert GeoJSON (single or multiple polygons) to custom format with bounds
 */
export function geoJSONToServiceArea(geoJSON: any): ServiceAreaPolygon | null {
  if (!geoJSON) {
    return null
  }

  // Handle FeatureCollection (multiple polygons)
  if (geoJSON.type === 'FeatureCollection' && geoJSON.features) {
    let polygons: [number, number][][] = []
    let allLats: number[] = []
    let allLngs: number[] = []

    geoJSON.features.forEach((feature: any) => {
      if (feature.geometry && feature.geometry.coordinates) {
        const coords = feature.geometry.coordinates[0]
        const polygon: [number, number][] = coords.map((coord: [number, number]) => [coord[1], coord[0]])

        // Ensure closed
        if (polygon.length > 0) {
          const first = polygon[0]
          const last = polygon[polygon.length - 1]
          if (first[0] !== last[0] || first[1] !== last[1]) {
            polygon.push([first[0], first[1]])
          }
        }

        polygons.push(polygon)
        allLats.push(...polygon.map((p) => p[0]))
        allLngs.push(...polygon.map((p) => p[1]))
      }
    })

    // Remove duplicates only (keep exact points from map)
    polygons = deduplicatePolygons(polygons)
    
    if (polygons.length === 0) return null

    // Recalculate lats/lngs after deduplication
    allLats = []
    allLngs = []
    polygons.forEach(polygon => {
      allLats.push(...polygon.map((p) => p[0]))
      allLngs.push(...polygon.map((p) => p[1]))
    })

    const centerLat = allLats.reduce((a, b) => a + b, 0) / allLats.length
    const centerLng = allLngs.reduce((a, b) => a + b, 0) / allLngs.length

    return {
      type: 'polygon',
      polygons: polygons,
      center: { lat: centerLat, lng: centerLng },
      bounds: {
        lat_min: Math.min(...allLats),
        lat_max: Math.max(...allLats),
        lng_min: Math.min(...allLngs),
        lng_max: Math.max(...allLngs),
      },
    }
  }

  // Handle single Feature
  if (geoJSON.geometry && geoJSON.geometry.coordinates) {
    const coords = geoJSON.geometry.coordinates[0]
    const polygon: [number, number][] = coords.map((coord: [number, number]) => [coord[1], coord[0]])

    // Ensure closed
    if (polygon.length > 0) {
      const first = polygon[0]
      const last = polygon[polygon.length - 1]
      if (first[0] !== last[0] || first[1] !== last[1]) {
        polygon.push([first[0], first[1]])
      }
    }

    const lats = polygon.map((p) => p[0])
    const lngs = polygon.map((p) => p[1])

    return {
      type: 'polygon',
      polygons: [polygon], // Wrap in array for consistency
      center: {
        lat: lats.reduce((a, b) => a + b, 0) / lats.length,
        lng: lngs.reduce((a, b) => a + b, 0) / lngs.length,
      },
      bounds: {
        lat_min: Math.min(...lats),
        lat_max: Math.max(...lats),
        lng_min: Math.min(...lngs),
        lng_max: Math.max(...lngs),
      },
    }
  }

  return null
}

/**
 * Convert custom format back to GeoJSON for map display
 */
export function serviceAreaToGeoJSON(serviceArea: ServiceArea): any | null {
  if (!serviceArea) {
    return null
  }

  if (serviceArea.type === 'polygon' && serviceArea.polygons) {
    // Multiple polygons - return FeatureCollection
    if (serviceArea.polygons.length > 1) {
      return {
        type: 'FeatureCollection',
        features: serviceArea.polygons.map((polygon) => ({
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [polygon.map((p) => [p[1], p[0]])],
          },
          properties: {},
        })),
      }
    }

    // Single polygon - return Feature
    if (serviceArea.polygons.length === 1) {
      return {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [serviceArea.polygons[0].map((p) => [p[1], p[0]])],
        },
        properties: {},
      }
    }
  }

  // Radius type - convert to polygon approximation for display
  if (serviceArea.type === 'radius' && serviceArea.center && serviceArea.radius) {
    // Create circle as polygon (32 points)
    const points = 32
    const radius = serviceArea.radius
    const centerLat = serviceArea.center.lat
    const centerLng = serviceArea.center.lng

    const coordinates: [number, number][] = []

    for (let i = 0; i <= points; i++) {
      const angle = (i / points) * 2 * Math.PI
      const dx = radius / 111 // ~111km per degree latitude
      const dy = radius / (111 * Math.cos((centerLat * Math.PI) / 180))

      const lat = centerLat + dx * Math.cos(angle)
      const lng = centerLng + dy * Math.sin(angle)

      coordinates.push([lng, lat]) // GeoJSON format: [lng, lat]
    }

    return {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [coordinates],
      },
      properties: {},
    }
  }

  return null
}

/**
 * Create default radius service area
 */
export function createDefaultRadiusArea(
  location: { lat: number; lng: number },
  radius: number = 30
): ServiceAreaRadius {
  const bounds = calculateCircleBounds(location.lat, location.lng, radius)

  return {
    type: 'radius',
    center: location,
    radius: radius,
    bounds: bounds,
  }
}

/**
 * Calculate bounding box for a circle
 */
export function calculateCircleBounds(lat: number, lng: number, radiusKm: number) {
  // 1 degree latitude ≈ 111 km
  // 1 degree longitude ≈ 111 km × cos(latitude)
  const latDelta = radiusKm / 111
  const lngDelta = radiusKm / (111 * Math.cos((lat * Math.PI) / 180))

  return {
    lat_min: lat - latDelta,
    lat_max: lat + latDelta,
    lng_min: lng - lngDelta,
    lng_max: lng + lngDelta,
  }
}

/**
 * Validate service area data
 */
export function validateServiceArea(serviceArea: any): boolean {
  if (!serviceArea || !serviceArea.type || !serviceArea.bounds) {
    return false
  }

  const { bounds } = serviceArea

  // Check bounds exist
  if (
    typeof bounds.lat_min !== 'number' ||
    typeof bounds.lat_max !== 'number' ||
    typeof bounds.lng_min !== 'number' ||
    typeof bounds.lng_max !== 'number'
  ) {
    return false
  }

  // Check Denmark range
  if (bounds.lat_min < 54 || bounds.lat_max > 58 || bounds.lng_min < 8 || bounds.lng_max > 15) {
    return false
  }

  // Type-specific validation
  if (serviceArea.type === 'radius') {
    if (!serviceArea.center || typeof serviceArea.radius !== 'number') {
      return false
    }
    if (serviceArea.radius < 1 || serviceArea.radius > 200) {
      return false
    }
  }

  if (serviceArea.type === 'polygon') {
    if (!Array.isArray(serviceArea.polygons) || serviceArea.polygons.length === 0) {
      return false
    }
    // Each polygon must have at least 4 points
    for (const polygon of serviceArea.polygons) {
      if (!Array.isArray(polygon) || polygon.length < 4) {
        return false
      }
    }
  }

  return true
}

